import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import validationErrorMsg from '../helpers/ValidationErrorMsg'
import { KTSVG } from '../../_metronic/helpers'
import Error from '../components/Error500'
import GetPagePermission from '../components/GetPagePermission'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

/**
 * Add a new field here to show it on Configs and wire it in SO screens.
 * key must match backend Project::salesOrderFieldVisibilityMap() camelCase alias.
 */
const SO_FIELD_DEFS = [
	{ key: 'showTransportationCost', label: 'Transportation Cost', shortLabel: 'Transport Cost', description: 'Per unit', defaultValue: 'Yes' },
	{ key: 'showEffectiveRate', label: 'Effective Rate', shortLabel: 'Effective Rate', description: 'Rate + transport', defaultValue: 'Yes' },
	{ key: 'showSrMaxDiscount', label: 'SR Max Discount (%)', shortLabel: 'SR Max Disc.', description: 'Read-only', defaultValue: 'Yes' },
	{ key: 'showAdminMaxDiscount', label: 'Admin Max Discount (%)', shortLabel: 'Admin Max Disc.', description: 'Read-only', defaultValue: 'Yes' },
	{ key: 'showDiscountPercent', label: 'Discount (%)', shortLabel: 'Discount %', description: 'Editable', defaultValue: 'Yes' },
	{ key: 'showDiscountAmount', label: 'Discount Amount (Per Unit)', shortLabel: 'Disc. Amount', description: 'Per unit discount', defaultValue: 'Yes' },
	{ key: 'showWeight', label: 'Weight (KG)', shortLabel: 'Weight', description: 'From product', defaultValue: 'No' },
	{ key: 'doublePrint', label: 'Double Print', shortLabel: 'Double Print', description: 'Print duplicate copy', defaultValue: 'No' },
	{ key: 'inventoryHardMaintain', label: 'Inventory Hard Maintain', shortLabel: 'Inv. Hard Maintain', description: 'Strict inventory control', defaultValue: 'No' },
] as const

/**
 * Numeric project configs. key must match Project::projectNumericConfigMap() camelCase alias.
 */
const NUMBER_FIELD_DEFS = [
	{ key: 'accountingBackdateDays', label: 'Accounting Backdate Days', shortLabel: 'Acc. Backdate', description: 'Max days allowed', defaultValue: 0 },
	{ key: 'inventoryBackdateDays', label: 'Inventory Backdate Days', shortLabel: 'Inv. Backdate', description: 'Max days allowed', defaultValue: 0 },
] as const

type FieldKey = (typeof SO_FIELD_DEFS)[number]['key']
type NumberFieldKey = (typeof NUMBER_FIELD_DEFS)[number]['key']

const parseBackdateDays = (value: string | number | undefined) => {
	const parsed = Number(value)
	return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0
}

interface ProjectFieldConfig {
	idProjects: number
	projectName: string
	projectShortName: string
	[key: string]: string | number
}

function AdminControlsConfigs() {
	const hasPermission = GetPagePermission('/configs')
	const [configs, setConfigs] = useState<ProjectFieldConfig[]>([])
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		getConfigs()
	}, [])

	const getConfigs = () => {
		setLoading(true)
		fetch(process.env.REACT_APP_API_URL + 'projects/sales-order-field-configs', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const rows = (resp.data || []).map((row: ProjectFieldConfig) => {
					const normalized = { ...row }
					SO_FIELD_DEFS.forEach((field) => {
						if (normalized[field.key] !== 'Yes' && normalized[field.key] !== 'No') {
							normalized[field.key] = field.defaultValue
						}
					})
					NUMBER_FIELD_DEFS.forEach((field) => {
						normalized[field.key] = parseBackdateDays(normalized[field.key])
					})
					return normalized
				})
				setConfigs(rows)
				setLoading(false)
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
				setLoading(false)
			})
	}

	const handleToggle = (index: number, field: FieldKey) => {
		const updated = [...configs]
		updated[index] = {
			...updated[index],
			[field]: updated[index][field] === 'Yes' ? 'No' : 'Yes',
		}
		setConfigs(updated)
	}

	const handleNumberChange = (index: number, field: NumberFieldKey, value: string) => {
		const updated = [...configs]
		updated[index] = {
			...updated[index],
			[field]: value === '' ? '' : parseBackdateDays(value),
		}
		setConfigs(updated)
	}

	const handleSubmit = (evt: React.FormEvent) => {
		evt.preventDefault()

		Swal.fire({
			title: 'Are you sure?',
			text: 'You want to save sales order field configurations',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'No',
			confirmButtonColor: '#ec008c',
		}).then((result) => {
			if (result.value) {
				const formData = {
					configs: configs.map((row) => {
						const payload: Record<string, string | number> = { idProjects: row.idProjects }
						SO_FIELD_DEFS.forEach((field) => {
							payload[field.key] = row[field.key] === 'Yes' ? 'Yes' : 'No'
						})
						NUMBER_FIELD_DEFS.forEach((field) => {
							payload[field.key] = parseBackdateDays(row[field.key])
						})
						return payload
					}),
				}

				fetch(
					process.env.REACT_APP_API_URL + 'projects/sales-order-field-configs',
					postRequestOptions(formData)
				)
					.then((resp) => resp.json())
					.then((resp) => {
						if (resp.success === true) {
							Swal.fire({
								icon: 'success',
								title: 'Success',
								text: resp.successMessage,
							})
							getConfigs()
						} else {
							const errorsMessage = validationErrorMsg(resp.errors).join('<br>')
							Swal.fire({
								icon: 'error',
								title: 'Error',
								html: errorsMessage,
							})
						}
					})
					.catch((error) => {
						console.log(error, 'catch the hoop')
					})
			}
		})
	}

	return (
		<>
			{hasPermission === 1 && (
				<div className='card card-custom'>
					<div className='card-header'>
						<div className='card-title'>
							<KTSVG
								path='/media/svg/icons/Code/Settings4.svg'
								className='svg-icon-1 svg-icon-primary'
							/>
							<span className='text fw-bolder' style={{ marginLeft: '10px' }}>
								Configs - Sales Order Field Visibility
							</span>
						</div>
					</div>

					<div className='card-body'>
						<p className='text-muted mb-4'>
							Enable or disable Sales Order / Quotation fields project-wise. Disabled fields are hidden on Create,
							Edit, Details, and Print.
						</p>

						{loading ? (
							<SkeletonTheme>
								<Skeleton count={8} />
							</SkeletonTheme>
						) : (
							<form onSubmit={handleSubmit}>
								<style>{`
									.so-field-config-wrap {
										overflow-x: auto;
										-webkit-overflow-scrolling: touch;
										border: 1px solid #eff2f5;
										border-radius: 0.475rem;
									}
									.so-field-config-table {
										margin-bottom: 0;
										min-width: 1180px;
									}
									.so-field-config-table th,
									.so-field-config-table td {
										vertical-align: middle !important;
										white-space: nowrap;
									}
									.so-field-config-table .sticky-col {
										position: sticky;
										left: 0;
										background: #fff;
										z-index: 2;
									}
									.so-field-config-table thead .sticky-col {
										background: #f9f9f9;
										z-index: 3;
									}
									.so-field-config-table .sticky-sl { left: 0; min-width: 48px; }
									.so-field-config-table .sticky-project { left: 48px; min-width: 200px; box-shadow: 4px 0 8px -4px rgba(0,0,0,.08); }
									.so-field-config-table .field-col { min-width: 120px; text-align: center; }
									.so-field-config-table .number-col { min-width: 140px; }
									.so-field-config-table .backdate-input {
										width: 80px;
										margin: 0 auto;
										text-align: center;
									}
									.so-field-config-table .field-label { font-size: 12px; font-weight: 600; line-height: 1.3; }
									.so-field-config-table .field-desc { display: block; font-size: 10px; font-weight: 400; color: #a1a5b7; margin-top: 2px; }
									@media (max-width: 991.98px) {
										.so-field-config-table .field-label-full { display: none; }
										.so-field-config-table .field-label-short { display: inline; }
									}
									@media (min-width: 992px) {
										.so-field-config-table .field-label-full { display: inline; }
										.so-field-config-table .field-label-short { display: none; }
									}
								`}</style>

								<div className='so-field-config-wrap'>
									<table className='table table-sm table-bordered table-hover so-field-config-table'>
										<thead className='table-light'>
											<tr>
												<th className='text-center sticky-col sticky-sl'>SL</th>
												<th className='sticky-col sticky-project'>Project</th>
												{SO_FIELD_DEFS.map((field) => (
													<th key={field.key} className='field-col'>
														<span className='field-label'>
															<span className='field-label-full'>{field.label}</span>
															<span className='field-label-short'>{field.shortLabel}</span>
														</span>
														<span className='field-desc'>{field.description}</span>
													</th>
												))}
												{NUMBER_FIELD_DEFS.map((field) => (
													<th key={field.key} className='field-col number-col'>
														<span className='field-label'>
															<span className='field-label-full'>{field.label}</span>
															<span className='field-label-short'>{field.shortLabel}</span>
														</span>
														<span className='field-desc'>{field.description}</span>
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{configs.map((row, index) => (
												<tr key={row.idProjects}>
													<td className='text-center sticky-col sticky-sl'>{index + 1}</td>
													<td className='sticky-col sticky-project'>
														<strong>{row.projectName}</strong>
														{row.projectShortName ? (
															<span className='text-muted'> ({row.projectShortName})</span>
														) : null}
													</td>
													{SO_FIELD_DEFS.map((field) => (
														<td key={field.key} className='field-col'>
															<label className='form-check form-switch form-check-custom form-check-solid justify-content-center mb-0'>
																<input
																	className='form-check-input'
																	type='checkbox'
																	checked={row[field.key] === 'Yes'}
																	onChange={() => handleToggle(index, field.key)}
																	aria-label={`${field.label} for ${row.projectName}`}
																/>
															</label>
														</td>
													))}
													{NUMBER_FIELD_DEFS.map((field) => (
														<td key={field.key} className='field-col number-col'>
															<input
																type='number'
																min={0}
																step={1}
																className='form-control form-control-sm backdate-input'
																value={row[field.key] ?? 0}
																onChange={(e) => handleNumberChange(index, field.key, e.target.value)}
																aria-label={`${field.label} for ${row.projectName}`}
															/>
														</td>
													))}
												</tr>
											))}
											{configs.length === 0 && (
												<tr>
													<td colSpan={2 + SO_FIELD_DEFS.length + NUMBER_FIELD_DEFS.length} className='text-center text-muted py-8'>
														No projects found.
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>

								{configs.length > 0 && (
									<div className='d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4'>
										<small className='text-muted'>
											{SO_FIELD_DEFS.length + NUMBER_FIELD_DEFS.length} configurable fields · Scroll horizontally on smaller screens
										</small>
										<input type='submit' className='btn btn-sm btn-success' value='Save' />
									</div>
								)}
							</form>
						)}
					</div>
				</div>
			)}
			{hasPermission === 0 && <Error />}
			{hasPermission === '' && (
				<div className='card card-custom' style={{ padding: '15px' }}>
					<SkeletonTheme>
						<p>
							<Skeleton containerClassName='avatar-skeleton' count={40}></Skeleton>
						</p>
					</SkeletonTheme>
				</div>
			)}
		</>
	)
}

export default AdminControlsConfigs
